import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { SerializedError } from '@reduxjs/toolkit';

interface UninitializedResult {
  isUninitialized: true;
  isLoading: false;
  isError: false;
  isSuccess: false;
}

interface SuccessfulResult<TData> {
  data: TData;
  isUninitialized: false;
  isLoading: false;
  isError: false;
  isSuccess: true;
}

interface ErrorResult<TError> {
  error: FetchBaseQueryError | SerializedError | TError;
  isUninitialized: false;
  isLoading: false;
  isError: true;
  isSuccess: false;
}

interface LoadingResult {
  isUninitialized: false;
  isLoading: true;
  isError: false;
  isSuccess: false;
}

export type AsyncResult<TData, TError = never> =
  | SuccessfulResult<TData>
  | ErrorResult<TError>
  | LoadingResult
  | UninitializedResult;

export const resultFactory = {
  successful: <TData>(data: TData): AsyncResult<TData> => ({
    isLoading: false,
    isUninitialized: false,
    isError: false,
    isSuccess: true,
    data,
  }),
  failed: <TError>(
    error: FetchBaseQueryError | SerializedError | TError,
  ): AsyncResult<never, FetchBaseQueryError | SerializedError | TError> => ({
    isLoading: false,
    isUninitialized: false,
    isError: true,
    isSuccess: false,
    error,
  }),
  loading: <TData = any>(): AsyncResult<TData> => ({
    isLoading: true,
    isUninitialized: false,
    isError: false,
    isSuccess: false,
  }),
  uninitialized: <TData = any>(): AsyncResult<TData> => ({
    isLoading: false,
    isUninitialized: true,
    isError: false,
    isSuccess: false,
  }),
  comibne: <TResult1, TResult2, TError, TCombinedResult>(
    ar1: AsyncResult<TResult1, TError>,
    ar2: AsyncResult<TResult2, TError>,
    f: (r1: TResult1, r2: TResult2) => TCombinedResult,
  ): AsyncResult<TCombinedResult, TError> => {
    if (ar1.isError) {
      return resultFactory.failed(ar1.error);
    }

    if (ar2.isError) {
      return resultFactory.failed(ar2.error);
    }

    if (ar1.isUninitialized || ar2.isUninitialized) {
      return resultFactory.uninitialized();
    }

    if (ar1.isLoading || ar2.isLoading) {
      return resultFactory.loading();
    }

    return resultFactory.successful(f(ar1.data, ar2.data));
  },
};
